import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import arrow from "@/assets/images/arrow.svg";

const SUPPORTED_LANGUAGES = [
  { title: "English", symbol: "en", emoji: "🇬🇧" },
  { title: "Spanish", symbol: "es", emoji: "🇪🇸" },
  { title: "Malay", symbol: "ms", emoji: "🇲🇾" },
  { title: "Swahili", symbol: "sw", emoji: "🌍" },
  { title: "Portuguese", symbol: "pt", emoji: "🇵🇹" },
  { title: "Hindi", symbol: "hi", emoji: "🇮🇳" },
  { title: "Indonesian", symbol: "id", emoji: "🇮🇩" },
  { title: "Thai", symbol: "th", emoji: "🇹🇭" },
  { title: "Filipino", symbol: "fil", emoji: "🇵🇭" },
];

type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["symbol"];

// Memoized Language Item Component
const LanguageItem = memo(function LanguageItem({
  symbol,
  title,
  emoji,
  isActive,
  onSelect,
}: {
  symbol: LanguageCode;
  title: string;
  emoji: string;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <li key={symbol} onClick={onSelect} className="px-4 cursor-pointer">
      <p
        className={clsx(
          "flex items-center justify-start gap-1 text-[14px] leading-5 font-medium text-white opacity-50 hover:opacity-100 transition-all whitespace-nowrap",
          {
            "!opacity-100": isActive,
          }
        )}
      >
        <span className="pt-0.5">{emoji}</span>
        <span>{title}</span>
      </p>
    </li>
  );
});

export const Language = memo(function Language() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { i18n } = useTranslation();

  const { emoji, symbol } = useMemo(() => {
    const active = SUPPORTED_LANGUAGES.find(
      (e) => e.symbol === (i18n.language || "en")
    );
    return { ...active };
  }, [i18n.language]);

  const toggle = useCallback(() => {
    setIsVisible((v) => !v);
  }, []);

  const changeLanguage = useCallback(
    (language: LanguageCode) => {
      return () => {
        localStorage.setItem("lang", language);
        i18n.changeLanguage(language);
        setIsVisible(false);
      };
    },
    [i18n]
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isVisible]);

  // Memoized language items to prevent unnecessary re-renders
  const languageItems = useMemo(() => {
    return SUPPORTED_LANGUAGES.map((language) => (
      <LanguageItem
        key={language.symbol}
        symbol={language.symbol}
        title={language.title}
        emoji={language.emoji}
        isActive={i18n.language === language.symbol}
        onSelect={changeLanguage(language.symbol)}
      />
    ));
  }, [i18n.language, changeLanguage]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        className="h-8 flex items-center gap-1 border border-[#24201C] rounded-full px-3"
        aria-expanded={isVisible}
        aria-haspopup="listbox"
        aria-label="select language"
      >
        <p className="text-[14px] leading-5 font-medium text-white whitespace-nowrap uppercase flex items-center justify-center">
          <span className="pr-1 pt-0.5">{emoji}</span>
          <span>{symbol}</span>
        </p>
        <img
          src={arrow}
          alt="arrow"
          width={16}
          height={16}
          loading="lazy"
          className={clsx("transition-transform", {
            "rotate-180": isVisible,
          })}
        />
      </button>

      {isVisible && (
        <div
          className="absolute z-10 top-9 right-0 bg-[#0C0703] transition-all duration-200 opacity-100"
          role="listbox"
          aria-label="Danh sách ngôn ngữ"
        >
          <ul className="border border-[#24201C] rounded-lg flex flex-col gap-3 py-3 min-w-[80px]">
            {languageItems}
          </ul>
        </div>
      )}
    </div>
  );
});
